import { observable, action, computed } from 'mobx';
import fetchDataHelper from '../helpers/fetchDataHelper';
import toastNotificationHelper from '../helpers/toastNotificationHelper';

export interface ISymbolNameDetails {
	symbol: string;
	pair: string;
	baseLong: string;
	quoteLong: string;
	baseShort: string;
	quoteShort: string;
}

export interface ISyncedSymbols {
	addSyncedSymbol: IAddSyncedSymbol;
	removeSyncedSymbol: IRemoveSyncedSymbol;
}

export interface IAddSyncedSymbol {
	symbols: string[];	
}

export interface IRemoveSyncedSymbol {
	symbols: string[];	
}

export interface ISymbolStore {
	inProgress: boolean;	
	symbolNameDetails: ISymbolNameDetails[];
	syncedSymbols: ISyncedSymbols;
	isSymbolSyncNeeded: boolean;
	initSymbolNameDetails(): Promise<void>; 
	getSyncedSymbols(): Promise<void>;
	saveSyncedSymbols(): Promise<void>;
    formatSymbol(symbol: string): string;
	formatChartSymbol(symbol: string): string;
	formatSymbolQuote(symbol: string): string;
	formatSymbolBase(symbol: string): string;
}

class SymbolStore implements ISymbolStore {

	@observable symbolNameDetails: ISymbolNameDetails[] = [];
	@observable syncedSymbols: ISyncedSymbols;
	@observable inProgress: boolean = false;

	@computed get isSymbolSyncNeeded(): boolean{
		return this.syncedSymbols != null && (this.syncedSymbols.addSyncedSymbol.symbols.length > 0 || this.syncedSymbols.removeSyncedSymbol.symbols.length > 0);
    }
	
	@action initSymbolNameDetails(): Promise<void> {
		return fetchDataHelper.getReturnJson("/symbol/getSymbols")
		.then(
			(result) => {	
				this.symbolNameDetails = result;
				
				this.inProgress = false;
				return Promise.resolve();
			},
			(error) => {
				toastNotificationHelper.notifyError('Init symbols failed');
				console.log('Init symbols failed: ' + error);
				this.inProgress = false;
				return Promise.reject();
			}
		);
	}

	@action getSyncedSymbols(): Promise<void> {
		return fetchDataHelper.getReturnJson("/symbol/getSyncedSymbols")
		.then(
			(result) => {			

				this.syncedSymbols = result;
				
				this.inProgress = false;
				return Promise.resolve();
			},
			(error) => {
				toastNotificationHelper.notifyError('Get synced symbols failed');
				console.log('Get synced symbols failed: ' + error);
				this.inProgress = false;
				return Promise.reject();
			}
		);
	}

	@action saveSyncedSymbols(): Promise<void> {
		return fetchDataHelper.post("/symbol/saveSyncedSymbols", this.syncedSymbols)
			.then(
				(result) => {
					this.getSyncedSymbols();
					
					this.inProgress = false;
					return Promise.resolve();
				},
				(error) => {
					toastNotificationHelper.notifyError('Save synced symbols failed');
					console.log('Save synced symbols failed: ' + error);
					this.inProgress = false;
					return Promise.reject();
				}
			);
	}

    @action.bound formatSymbol(symbol: string): string {
		var symbolDetailName = this.symbolNameDetails.find(snd => snd.symbol == symbol);
		return symbolDetailName != null ? symbolDetailName.pair : symbol;
	}

	@action.bound formatChartSymbol(symbol: string): string {
		var symbolDetailName = this.symbolNameDetails.find(snd => snd.symbol == symbol);
		return symbolDetailName != null ? symbolDetailName.baseShort + symbolDetailName.quoteShort : symbol;
	}

	@action.bound formatSymbolQuote(symbol: string): string {
		var symbolDetailName = this.symbolNameDetails.find(snd => snd.symbol == symbol);
		return symbolDetailName != null ? symbolDetailName.quoteLong : 'Quote';
	}

	@action.bound formatSymbolBase(symbol: string): string {
		var symbolDetailName = this.symbolNameDetails.find(snd => snd.symbol == symbol);
		return symbolDetailName != null ? symbolDetailName.baseLong : 'Base';
	}
}

export default new SymbolStore();